import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { IstrapiRole } from '../interfaces/apiCalls';
import { frontendRoles } from '../interfaces/apiCalls';
import { ApiService } from '../services/api/api.service';
import { StateService } from '../@core/utils';
import { awaitRolesLoaded } from '../common/utils';

export interface ValidationResult<ValidatorExtras = {}> {
    strong?: boolean;
    asyncError?: boolean;
    valid: boolean,
    failedValidator: string;
    details?: ValidatorExtras;
}

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 50;

export interface PasswordStrengthDetails {
    hasNumber: boolean;
    hasUpper: boolean;
    hasLower: boolean;
    hasSpecial: boolean;
    hasMinLength?: boolean; // at least 8 characters
    hasMaxLength?: boolean; // at most 50 characters
}

export class PasswordValidator {

    public static strong(control: FormControl): ValidationResult {
        let hasNumber = /\d/.test(control.value);
        let hasUpper = /[A-Z]/.test(control.value);
        let hasLower = /[a-z]/.test(control.value);
        let hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(control.value);

        // console.log('Num, Upp, Low', hasNumber, hasUpper, hasLower, hasSpecial);
        const valid = hasNumber && hasUpper && hasLower && hasSpecial;
        if (!valid) {
            // return what´s not valid
            return { strong: false, valid: false, failedValidator: "strong"};
        }
        return null;
    }

    public static strongDetailed(control: FormControl): ValidationResult<PasswordStrengthDetails> {
        let hasNumber = /\d/.test(control.value);
        let hasUpper = /[A-Z]/.test(control.value);
        let hasLower = /[a-z]/.test(control.value);
        let hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(control.value);
        let hasMinLength = control.value && control.value.length >= PASSWORD_MIN_LENGTH;
        let hasMaxLength = control.value && control.value.length <= PASSWORD_MAX_LENGTH;

        // console.log('Num, Upp, Low', hasNumber, hasUpper, hasLower, hasSpecial);
        const valid = hasNumber && hasUpper && hasLower && hasSpecial && hasMinLength && hasMaxLength;
        if (!valid) {
            // return what´s not valid
            return { strong: false, valid: false, failedValidator: "strongDetailed", details: { hasNumber, hasUpper, hasLower, hasSpecial, hasMaxLength, hasMinLength } };
        }
        return null;
    }


    public static passwordMatchValidator(formGroup: FormGroup): ValidationResult {
        const password = formGroup.get('password')?.value;
        const passwordConfirmation = formGroup.get('passwordConfirmation')?.value;
        
        const result = password === passwordConfirmation ? null : { valid: false, failedValidator: "passwordMatchValidator" };
        return result
      }

    public static passwordIsDifferentValidator(formGroup: FormGroup): ValidationResult {
        const currentPassword = formGroup.get('currentPassword')?.value;
        const password = formGroup.get('password')?.value;
        const passwordConfirmation = formGroup.get('passwordConfirmation')?.value;
        
        const result = ( !!currentPassword && currentPassword.length > 0 && currentPassword === password && password === passwordConfirmation) ? { valid: false, failedValidator: "passwordIsDifferentValidator" } : null ;
        return result
      }

}
// Luxembourg matricule (national ID) validation.
//
// A matricule is 13 digits: an 11-digit base (YYYYMMDD + 3-digit sequence)
// followed by a Luhn (mod-10) check digit and a Verhoeff check digit, both
// computed over the same 11-digit base.

// --- Standard Verhoeff tables -----------------------------------------------
const VERHOEFF_D = [
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
    [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
    [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
    [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
    [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
    [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
    [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
    [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
    [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];

const VERHOEFF_P = [
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
    [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
    [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
    [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
    [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
    [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
    [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];

const VERHOEFF_INV = [0, 4, 3, 2, 1, 5, 6, 7, 8, 9];

const verhoeffDigit = (base: string): number => {
    const arr = base.split('').reverse().map(Number);
    let c = 0;
    for (let j = 0; j < arr.length; j++) {
        c = VERHOEFF_D[c][VERHOEFF_P[(j + 1) % 8][arr[j]]];
    }
    return VERHOEFF_INV[c];
};

const luhnDigit = (base: string): number => {
    const digits = base + '0';
    let sum = 0;
    let k = 0;
    for (let i = digits.length - 1; i >= 0; i--) {
        k++;
        let j = Number(digits[i]);
        if (k % 2 === 0) {
            j *= 2;
            if (j > 9) j -= 9;
        }
        sum += j;
    }
    return (10 - (sum % 10)) % 10;
};

/**
 * Validate a 13-digit Luxembourg matricule (format + Luhn + Verhoeff check digits).
 * @param matricule the matricule to check; whitespace is ignored.
 * @returns true if the matricule is structurally valid.
 */
export const validateMatricule = (matricule: string | number): boolean => {
    const m = String(matricule ?? '').replace(/\s+/g, '');
    if (!/^\d{13}$/.test(m)) return false;
    const base = m.slice(0, 11);
    return m.slice(11) === `${luhnDigit(base)}${verhoeffDigit(base)}`;
};

export class MatriculeValidator {

    /**
     * Angular validator for a required matricule field.
     * Empty values fail here; combine with Validators.required for the "missing" message.
     */
    public static validateMatricule(control: FormControl): ValidationResult {
        if (!validateMatricule(control.value)) {
            return { valid: false, failedValidator: "validateMatricule" };
        }
        return null;
    }

    /**
     * Same check, but an empty value passes — for optional matricule fields.
     */
    public static validateMatriculeOptional(control: FormControl): ValidationResult {
        const value = control.value;
        if (value === null || value === undefined || String(value).trim() === '') {
            return null;
        }
        return MatriculeValidator.validateMatricule(control);
    }
}

export class BoolValidator {

    public static validateBool(control: FormControl): ValidationResult {
        
        const valid = typeof control.value === 'boolean';
        if (!valid) {
            // return what´s not valid
            return { valid: false, failedValidator: "validateBool"};
        }
        return null;
    }
}

export class RoleValidator {

public static asyncGetRoleExistsValidator(stateRef: StateService) {
        
    return (control: FormControl): Promise<ValidationResult> => {
       
        return new Promise<ValidationResult>( async (resolve, reject) => {
            let existingRoles: IstrapiRole[];
            try {
                existingRoles = await awaitRolesLoaded(stateRef)
            } catch (error) {
                // roles couldn't be fetched: don't block the form, the backend validates the role anyway
                console.deverror("Couldn't load roles for role validation:", error);
                resolve(null);
                return;
            }
            let onlyRoleNames = existingRoles.map(role => role.name.toLowerCase())
            const valid = onlyRoleNames.includes(String(control.value ?? '').toLowerCase());
            resolve(valid ? null : { valid: false, failedValidator: "validateRoleExists"});
        });
    }
}

// Outdated, use asyncGetRoleExistsValidator !
public static getRoleExistsValidator(existingRoles: IstrapiRole[]) {

    let onlyRoleNames = existingRoles.map(role => role.name.toLowerCase())

        // console.log("Inside RoleValidator.validateRoleExists, control.value, valid)", control.value, valid);
    return (control: FormControl): ValidationResult => {

        const valid = onlyRoleNames.includes(String(control.value?? '').toLowerCase());
        return valid ? null : { valid: false, failedValidator: "validateRoleExists" };
    }
}
    public static validateRoleExists(control: FormControl): ValidationResult {

        const valid = frontendRoles.includes(control.value);
        if (!valid) {
            // return what´s not valid
            return { valid: false, failedValidator: "validateRoleExists"};
        }
        return null;
    }
}

export class DateValidator {

    // used on the eCit component
    public static ecitDateValidator = (control: AbstractControl) => {
        const v = control.value;
        if (v == null || v === '') return null;
        return v instanceof Date && !isNaN(v.getTime()) ? null : { date: true };
    };

    public static validateDateFormat(control: FormControl): ValidationResult {

        let validDate = /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/.test(control.value);
        if (!validDate) {
            // return what´s not valid
            return { valid: false, failedValidator: "validateDateFormat"};
        }
        return null;
    }

    public static validateDateBefore(control: FormControl): ValidationResult {

        // check if the received value is today or earlier
        let date = new Date(control.value)
        let now = new Date()
        if (date > now) {
            console.devlog("date is in the future")
            return { valid: false, failedValidator: "validateDateBefore"};
        } else if(date.getFullYear() < 1900){
            console.devlog("date.getFullYear()", date.getFullYear())
            console.devlog("date is below 1900")
            return { valid: false, failedValidator: "validateDateBefore"};
        }
        return null;
    }
    public static validateDateAfter(control: FormControl): ValidationResult {
        // check if the received value is today or later
        let date = new Date(control.value)
        let now = new Date()
        //console.log("date", date)
        //console.log("now", now)
        
        if (date <= now) {
            //console.log("date is not in the future")
            return { valid: false, failedValidator: "validateDateAfter"};
        }
        return null;
    }
}


export const getMaxDecimalsValidatorFn = (maxDecimals: number) =>{

    return (control : FormControl) : ValidationResult => {
        let hasDecimals = control.value.toString().split(".")[1] ?? control.value.toString().split(",")[1] 
        if (hasDecimals && hasDecimals.length > maxDecimals) {
            return { valid: false, failedValidator: "getMaxDecimalsValidatorFn"};
        }
        return null;
    }
}



// use factory function pattern, see https://angular.io/guide/form-validation#custom-validators
// @param allowThisId: if this id is passed, the function will allow the code to be the same as the code on the entry with this id passed as param
// reasoning: validator check needs to succeed when just editing an entry.
export const getValidateTarifCodeTakenOrNotFunction = (requireExistence: boolean, api : ApiService, allowThisId?: string) => {
    return (control: FormControl) : Promise<ValidationResult>  => {
        
        return new Promise( async (resolve, reject) => { 
            
            if (control.value.length < 1) {
                resolve( { asyncError: true, valid: false ,failedValidator: "getValidateTarifCodeTakenOrNotFunction" });
            } else {    
                    let res : {documentId:string, exists: boolean} = await api.checkIfCodeExists(control.value)
                    if (res.exists !== requireExistence && allowThisId != res.documentId) {
                        resolve( { asyncError: true, valid: false ,failedValidator: "getValidateTarifCodeTakenOrNotFunction" });
                    } else if(requireExistence && !res.exists) {
                        resolve( { asyncError: true, valid: false ,failedValidator: "getValidateTarifCodeTakenOrNotFunction" });
                    }
                      else { 
                        resolve(null);
                    }
                
            }
        })
        
    }
}

// use factory function pattern, see https://angular.io/guide/form-validation#custom-validators
// @param allowThisId: if this id is passed, the function will allow the suffix to be the same as the suffix on the entry with this id passed as param
// reasoning: validator check needs to succeed when just editing an entry.
// this function checks if a certain suffix with a date already exists, to prevent duplicates

export const getValidateSuffixAndDateUniqueOrNotFunction = (api : ApiService, formGroupRef: any, allowThisId?: string) => {
    return (control: FormControl) : Promise<ValidationResult>  => {

        // pass the reference to the form so that the validator function can access its fields
        var validFrom;
        if(formGroupRef.controls["validFrom"]) {
            // for create mode
            validFrom = formGroupRef.controls["validFrom"].value
         } else {
            // for edit mode
            validFrom = formGroupRef.controls["validFrom-visible"].value
        }

        let suffix = formGroupRef.controls["suffix"].value

        return new Promise( async (resolve, reject) => { 

            // if either the suffix or the date is not set, we don't need to check.
            if(!validFrom || !suffix) {
                resolve(null);
                return
            }
            
            if (control.value.length < 1) {
                resolve( { asyncError: true, valid: false ,failedValidator: "getValidateSuffixAndDateUniqueOrNotFunction" });
            } else {    
                    let res : {documentId:string, exists: boolean} = await api.checkIfSuffixWithDateExists(suffix, validFrom)
                    if (res.exists && allowThisId != res.documentId) {
                    console.devlog("res", res)
                    console.devlog("allowThisId", allowThisId)
                        resolve( { asyncError: true, valid: false ,failedValidator: "getValidateSuffixAndDateUniqueOrNotFunction" });
                    } 
                      else { 
                        resolve(null);
                    }
                
            }
        })
        
    }
}

// because of weird timing issues in creation of the FormGroup, this slightly modified implementation of the validator function was necessary.
export const getValidateSuffixAndDateUniqueOrNotFunction_two = (api : ApiService, formGroupRef: any, allowThisId?: string) => {


    return (control: FormControl) : Promise<ValidationResult> => {

        return new Promise( async (resolve, reject) => { 
            // pass the reference to the form so that the validator function can access its fields
            var validFrom;


            if(formGroupRef.controls["validFrom"]) {
                // for create mode
                validFrom = formGroupRef.controls["validFrom"].value
             } else if (formGroupRef.controls["validFrom-visible"]){
                // for edit mode
                validFrom = formGroupRef.controls["validFrom-visible"].value
            } else {
                return
            }
    
            let suffix = formGroupRef.controls["suffix"].value
    
       
            // if either the suffix or the date is not set, we don't need to check.
            if(!validFrom || !suffix) {
                resolve(null);
                return
            }
            
            if (control.value.length < 1) {
                resolve( { asyncError: true, valid: false ,failedValidator: "getValidateSuffixAndDateUniqueOrNotFunction" });
            } else {    
                    let res : {documentId:string, exists: boolean} = await api.checkIfSuffixWithDateExists(suffix, validFrom)
                    console.devlog("allowThisId", allowThisId)
                    if (res.exists && allowThisId != res.documentId) {
                        resolve( { asyncError: true, valid: false ,failedValidator: "getValidateSuffixAndDateUniqueOrNotFunction" });
                    } 
                      else { 
                        resolve(null);
                    }
                
            }
        })
        
    }
}

// use factory function pattern, see https://angular.io/guide/form-validation#custom-validators
// @param allowThisId: if this id is passed, the function will allow the suffix to be the same as the suffix on the entry with this id passed as param
// reasoning: validator check needs to succeed when just editing an entry.
// this function checks if a certain suffix with a date already exists, to prevent duplicates

export const getValidateNomenclatureIDAndDateUniqueOrNotFunction = (api : ApiService, formGroupRef: any, allowThisId?: string) => {
    return (control: FormControl) : Promise<ValidationResult>  => {

        // pass the reference to the form so that the validator function can access its fields
        var validFrom;
        if(formGroupRef.controls["validFrom"]) {
            // for create mode
            validFrom = formGroupRef.controls["validFrom"].value
         } else {
            // for edit mode
             validFrom = formGroupRef.controls["validFrom-visible"].value
        }

        // mitigate racing conditions between the form being put together and the validator being called
        var nomenclatureID;
        try{ 
            nomenclatureID = formGroupRef.controls["nomenclatureID"].value
        } catch(e){
            setTimeout(() => {
                nomenclatureID = formGroupRef.controls["nomenclatureID"].value
            }, 500)
        }

        return new Promise( async (resolve, reject) => { 

            // if either the suffix or the date is not set, we don't need to check.
            if(!validFrom || !nomenclatureID) {
                resolve(null);
                return
            }
            
            if (control.value.length < 1) {
                resolve( { asyncError: true, valid: false ,failedValidator: "getValidateNomenclatureIDAndDateUniqueOrNotFunction" });
            } else {    
                    let res : {documentId:string, exists: boolean} = await api.checkIfIndexWithDateExists(nomenclatureID, validFrom)
                    if (res.exists && allowThisId != res.documentId) {
                    console.devlog("res", res)
                    console.devlog("allowThisId", allowThisId)
                        resolve( { asyncError: true, valid: false ,failedValidator: "getValidateNomenclatureIDAndDateUniqueOrNotFunction"});
                    } 
                      else { 
                        resolve(null);
                    }
                
            }
        })
        
    }
}

// because of weird timing issues in creation of the FormGroup, this slightly modified implementation of the validator function was necessary.
export const getValidateNomenclatureIDAndDateUniqueOrNotFunction_two = (api : ApiService, formGroupRef: any, allowThisId?: string) => {


    return (control: FormControl) : Promise<ValidationResult> => {

        return new Promise( async (resolve, reject) => { 
            // pass the reference to the form so that the validator function can access its fields
            var validFrom;


            if(formGroupRef.controls["validFrom"]) {
                // for create mode
                validFrom = formGroupRef.controls["validFrom"].value
             } else if (formGroupRef.controls["validFrom-visible"]){
                // for edit mode
                formGroupRef.controls["validFrom-visible"].value
            } else {
                return
            }
    
                 // mitigate racing conditions between the form being put together and the validator being called
        var nomenclatureID;
        try{ 
            nomenclatureID = formGroupRef.controls["nomenclatureID"].value
        } catch(e){
            setTimeout(() => {
                nomenclatureID = formGroupRef.controls["nomenclatureID"].value
            }, 500)
        }
       
            // if either the suffix or the date is not set, we don't need to check.
            if(!validFrom || !nomenclatureID) {
                resolve(null);
                return
            }
            
            if (control.value.length < 1) {
                resolve( { asyncError: true, valid: false ,failedValidator: "getValidateNomenclatureIDAndDateUniqueOrNotFunction" });
            } else {    
                    let res : {documentId: string, exists: boolean} = await api.checkIfIndexWithDateExists(nomenclatureID, validFrom)
                    console.devlog("allowThisId", allowThisId)
                    if (res.exists && allowThisId != res.documentId) {
                        resolve( { asyncError: true, valid: false ,failedValidator: "getValidateNomenclatureIDAndDateUniqueOrNotFunction" });
                    } 
                      else { 
                        resolve(null);
                    }
            }
        })
        
    }
}